import React, { useEffect, useRef } from 'react';

const COLORS_LIGHT = [
  'rgba(78, 116, 244, 0.45)',
  'rgba(78, 116, 244, 0.28)',
  'rgba(104, 198, 187, 0.42)',
  'rgba(104, 198, 187, 0.26)',
  'rgba(120, 150, 245, 0.22)',
];

const COLORS_DARK = [
  'rgba(78, 116, 244, 0.28)',
  'rgba(78, 116, 244, 0.16)',
  'rgba(104, 198, 187, 0.24)',
  'rgba(104, 198, 187, 0.14)',
  'rgba(120, 150, 245, 0.12)',
];

const PARTICLE_COUNT = 32;

function isDarkTheme() {
  return document.documentElement.getAttribute('data-theme') === 'dark';
}

function createParticles(width, height) {
  const colors = isDarkTheme() ? COLORS_DARK : COLORS_LIGHT;
  return Array.from({ length: PARTICLE_COUNT }, () => ({
    x: Math.random() * width,
    y: Math.random() * height,
    r: 1.2 + Math.random() * 2.8,
    vx: (Math.random() - 0.5) * 0.22,
    vy: (Math.random() - 0.5) * 0.18,
    color: colors[Math.floor(Math.random() * colors.length)],
    pulse: Math.random() * Math.PI * 2,
    pulseSpeed: 0.008 + Math.random() * 0.012,
  }));
}

/**
 * Soft floating orbs for the Home hero. pointer-events-none; pauses when tab hidden.
 */
const HeroParticles = () => {
  const canvasRef = useRef(null);
  const mouseRef = useRef({ x: 0.5, y: 0.5 });
  const particlesRef = useRef([]);
  const rafRef = useRef(0);
  const visibleRef = useRef(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = parent.clientWidth;
      const h = parent.clientHeight;
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      if (particlesRef.current.length === 0) {
        particlesRef.current = createParticles(w, h);
      } else {
        particlesRef.current.forEach((p) => {
          p.x = Math.min(p.x, w);
          p.y = Math.min(p.y, h);
        });
      }
    };

    const refreshColors = () => {
      const colors = isDarkTheme() ? COLORS_DARK : COLORS_LIGHT;
      particlesRef.current.forEach((p) => {
        p.color = colors[Math.floor(Math.random() * colors.length)];
      });
    };

    const onMouseMove = (e) => {
      const parent = canvas.parentElement;
      if (!parent) return;
      const rect = parent.getBoundingClientRect();
      mouseRef.current = {
        x: (e.clientX - rect.left) / Math.max(rect.width, 1),
        y: (e.clientY - rect.top) / Math.max(rect.height, 1),
      };
    };

    const onVisibility = () => {
      visibleRef.current = document.visibilityState === 'visible';
      if (visibleRef.current) {
        rafRef.current = requestAnimationFrame(draw);
      }
    };

    const themeObserver = new MutationObserver(refreshColors);
    themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });

    const draw = () => {
      if (!visibleRef.current) return;

      const parent = canvas.parentElement;
      if (!parent) return;
      const w = parent.clientWidth;
      const h = parent.clientHeight;
      const mx = (mouseRef.current.x - 0.5) * 18;
      const my = (mouseRef.current.y - 0.5) * 14;

      ctx.clearRect(0, 0, w, h);

      for (const p of particlesRef.current) {
        p.x += p.vx;
        p.y += p.vy;
        p.pulse += p.pulseSpeed;

        if (p.x < -10) p.x = w + 10;
        if (p.x > w + 10) p.x = -10;
        if (p.y < -10) p.y = h + 10;
        if (p.y > h + 10) p.y = -10;

        const drawX = p.x + mx * (p.r / 4);
        const drawY = p.y + my * (p.r / 4);
        const alphaPulse = 0.65 + Math.sin(p.pulse) * 0.35;
        const radius = p.r * (0.85 + Math.sin(p.pulse * 0.7) * 0.15);

        ctx.beginPath();
        ctx.arc(drawX, drawY, radius, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = alphaPulse;
        ctx.fill();

        // Soft halo for a few larger orbs
        if (p.r > 2.8) {
          ctx.beginPath();
          ctx.arc(drawX, drawY, radius * 2.4, 0, Math.PI * 2);
          ctx.fillStyle = p.color;
          ctx.globalAlpha = alphaPulse * 0.18;
          ctx.fill();
        }
      }

      ctx.globalAlpha = 1;
      rafRef.current = requestAnimationFrame(draw);
    };

    resize();
    rafRef.current = requestAnimationFrame(draw);

    window.addEventListener('resize', resize);
    window.addEventListener('mousemove', onMouseMove, { passive: true });
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('visibilitychange', onVisibility);
      themeObserver.disconnect();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="hero-particles-canvas"
      aria-hidden="true"
    />
  );
};

export default HeroParticles;
